import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { carePlanService } from '../services/carePlanService'
import { userService } from '../services/userService'
import { patientService } from '../services/patientService'
import { CarePlanStatus } from '../types/carePlan'
import { CarePlanHistory } from '../components/CarePlanHistory'
import { CarePlanTransferModal } from '../components/CarePlanTransferModal'
import { CarePlanForm } from '../components/CarePlanForm'
import { CarePlanCreateTemplateModal } from '../components/CarePlanCreateTemplateModal'
import { Modal } from '../components/Modal'
import { formatDate } from '../utils/locale'
import { formatUserName } from '../utils/name'
import { User } from '../types/user'
import './CarePlanDetail.css'

export function CarePlanDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showEditForm, setShowEditForm] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false)
  const [activeTab, setActiveTab] = useState<'overview' | 'history'>('overview')

  const { data: carePlan, isLoading, error } = useQuery({
    queryKey: ['care-plan', id],
    queryFn: () => carePlanService.getById(id!),
    enabled: !!id,
  })

  // Fetch patient information for print view
  const { data: patient } = useQuery({
    queryKey: ['patient', carePlan?.patientId],
    queryFn: () => patientService.getById(carePlan!.patientId!),
    enabled: !!carePlan?.patientId,
  })

  // Fetch all users to resolve provider names
  const { data: allUsers } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: () => userService.getAll(),
  })

  // Get current provider (transferredTo if transferred, otherwise createdBy)
  const currentProviderId = useMemo(() => {
    if (!carePlan) return null
    return carePlan.transferredTo || carePlan.createdBy || null
  }, [carePlan])

  // Get provider user object
  const currentProvider = useMemo(() => {
    if (!currentProviderId || !allUsers) return null
    return allUsers.find((u) => u.id === currentProviderId) || null
  }, [currentProviderId, allUsers])

  // Get provider display name
  const providerDisplayName = useMemo(() => {
    if (!currentProvider) return null
    return formatUserName(currentProvider)
  }, [currentProvider])

  const deleteMutation = useMutation({
    mutationFn: () => carePlanService.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['care-plans'] })
      if (carePlan?.patientId) {
        navigate(`/patients/${carePlan.patientId}`)
      } else {
        navigate('/patients')
      }
    },
  })

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this care plan? This action cannot be undone.')) {
      try {
        await deleteMutation.mutateAsync()
      } catch (error) {
        alert('Failed to delete care plan')
      }
    }
  }

  const getStatusBadge = (status: CarePlanStatus) => {
    const statusClasses = {
      draft: 'status-draft',
      active: 'status-active',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
    }
    return <span className={`status-badge ${statusClasses[status]}`}>{status}</span>
  }

  if (isLoading) return <div className="loading">Loading care plan...</div>
  if (error) return <div className="error">Error loading care plan</div>
  if (!carePlan) return <div>Care plan not found</div>

  return (
    <div className="care-plan-detail">
      <div className="care-plan-detail-header">
        <div className="header-top">
          <button
            className="btn-back"
            onClick={() => {
              if (carePlan.patientId) {
                // Navigate to patient detail page with care-plans hash
                // PatientDetail component will handle the hash and set the active tab
                navigate(`/patients/${carePlan.patientId}#care-plans`)
              } else {
                navigate('/patients')
              }
            }}
          >
            ← Back to {carePlan.patientId ? 'Patient' : 'Patients'}
          </button>
          <div className="header-actions">
          {!carePlan.isTemplate && (
            <button className="btn-secondary" onClick={() => window.print()}>
              Print
            </button>
          )}
          {carePlan.status !== 'cancelled' && !carePlan.isTemplate && (
            <button className="btn-secondary" onClick={() => setShowTransferModal(true)}>
              Transfer to Provider
            </button>
          )}
          {!carePlan.isTemplate && (
            <>
              <button className="btn-secondary" onClick={() => setShowCreateTemplateModal(true)}>
                Create Template
              </button>
              <button className="btn-secondary" onClick={() => setShowEditForm(true)}>
                Edit
              </button>
              <button className="btn-danger" onClick={handleDelete} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </>
          )}
          </div>
        </div>
        <div className="header-title-section">
          <h1>{carePlan.title}</h1>
          <div className="care-plan-meta">
            <div className="meta-row">
              {carePlan.startDate && (
                <span className="care-plan-date">
                  Started: {formatDate(carePlan.startDate)}
                </span>
              )}
              {getStatusBadge(carePlan.status)}
              {carePlan.templateId && (
                <span className="care-plan-template-badge">Created from Template</span>
              )}
            </div>
            {providerDisplayName && (
              <div className="provider-info">
                <span className="provider-label">
                  {carePlan.transferredTo ? 'Transferred to' : 'Provider'}:{' '}
                </span>
                <span className="provider-name">{providerDisplayName}</span>
                {carePlan.transferredAt && (
                  <span className="transfer-date">
                    {' '}on {formatDate(carePlan.transferredAt)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="care-plan-content">
        <div className="care-plan-tabs">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          {!carePlan.isTemplate && (
            <button
              className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              History
            </button>
          )}
        </div>

        {activeTab === 'overview' && (
          <div className="care-plan-main">
            {/* Print header - only visible when printing */}
            <div className="print-header" style={{ display: 'none' }}>
              <h1>{carePlan.title}</h1>
              <div className="print-meta">
                {patient && (
                  <div className="print-meta-item">
                    <span className="print-meta-label">Patient:</span>
                    <span>{patient.firstName} {patient.lastName}</span>
                    {patient.dateOfBirth && (
                      <span> (DOB: {formatDate(patient.dateOfBirth)})</span>
                    )}
                  </div>
                )}
                {carePlan.startDate && (
                  <div className="print-meta-item">
                    <span className="print-meta-label">Start Date:</span>
                    <span>{formatDate(carePlan.startDate)}</span>
                  </div>
                )}
                {carePlan.endDate && (
                  <div className="print-meta-item">
                    <span className="print-meta-label">End Date:</span>
                    <span>{formatDate(carePlan.endDate)}</span>
                  </div>
                )}
                {carePlan.reviewDate && (
                  <div className="print-meta-item">
                    <span className="print-meta-label">Review Date:</span>
                    <span>{formatDate(carePlan.reviewDate)}</span>
                  </div>
                )}
                <div className="print-meta-item">
                  <span className="print-meta-label">Status:</span>
                  <span>{carePlan.status}</span>
                </div>
                {providerDisplayName && (
                  <div className="print-meta-item">
                    <span className="print-meta-label">
                      {carePlan.transferredTo ? 'Transferred to' : 'Provider'}:
                    </span>
                    <span>{providerDisplayName}</span>
                  </div>
                )}
                <div className="print-meta-item">
                  <span className="print-meta-label">Printed:</span>
                  <span>{formatDate(new Date().toISOString())}</span>
                </div>
              </div>
            </div>

            {/* Patient info block for print */}
            {patient && (
              <div className="print-patient-info" style={{ display: 'none' }}>
                <strong>Patient Information</strong>
                <div className="patient-line">
                  <strong>Name:</strong> {patient.firstName} {patient.lastName}
                </div>
                {patient.dateOfBirth && (
                  <div className="patient-line">
                    <strong>Date of Birth:</strong> {formatDate(patient.dateOfBirth)}
                  </div>
                )}
                {patient.patientNumber && (
                  <div className="patient-line">
                    <strong>Patient Number:</strong> {patient.patientNumber}
                  </div>
                )}
                {patient.ppsNumber && (
                  <div className="patient-line">
                    <strong>PPS Number:</strong> {patient.ppsNumber}
                  </div>
                )}
              </div>
            )}

            <div className="care-plan-section">
              <h2>Plan Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>Status</label>
                  <p>{getStatusBadge(carePlan.status)}</p>
                </div>
                {carePlan.startDate && (
                  <div className="info-item">
                    <label>Start Date</label>
                    <p>{formatDate(carePlan.startDate)}</p>
                  </div>
                )}
                {carePlan.endDate && (
                  <div className="info-item">
                    <label>End Date</label>
                    <p>{formatDate(carePlan.endDate)}</p>
                  </div>
                )}
                {carePlan.reviewDate && (
                  <div className="info-item">
                    <label>Review Date</label>
                    <p>{formatDate(carePlan.reviewDate)}</p>
                  </div>
                )}
                {carePlan.templateId && (
                  <div className="info-item">
                    <label>Template</label>
                    <p>Created from template</p>
                  </div>
                )}
                {carePlan.transferredTo && (
                  <div className="info-item full-width">
                    <label>Transferred To</label>
                    <p>
                      Transferred to provider on {carePlan.transferredAt && formatDate(carePlan.transferredAt)}
                      {carePlan.transferReason && ` - ${carePlan.transferReason}`}
                    </p>
                  </div>
                )}
                {carePlan.description && (
                  <div className="info-item full-width">
                    <label>Description</label>
                    <div className="description-content">{carePlan.description}</div>
                  </div>
                )}
                {carePlan.notes && (
                  <div className="info-item full-width">
                    <label>Notes</label>
                    <div className="notes-content">{carePlan.notes}</div>
                  </div>
                )}
              </div>
            </div>

            {carePlan.goals && carePlan.goals.length > 0 && (
              <div className="care-plan-section">
                <h2>Goals ({carePlan.goals.length})</h2>
                {/* Screen view - list format */}
                <div className="goals-list screen-only">
                  {carePlan.goals.map((goal) => (
                    <div key={goal.id} className="goal-item">
                      <div className="goal-header">
                        <span className={`goal-status status-${goal.status}`}>{goal.status}</span>
                        {goal.targetDate && (
                          <span className="goal-target-date">Target: {formatDate(goal.targetDate)}</span>
                        )}
                      </div>
                      <div className="goal-description">{goal.description}</div>
                      {goal.notes && <div className="goal-notes">{goal.notes}</div>}
                      {goal.achievedDate && (
                        <div className="goal-achieved">Achieved: {formatDate(goal.achievedDate)}</div>
                      )}
                    </div>
                  ))}
                </div>
                {/* Print view - table format */}
                <table className="print-table print-only">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Target Date</th>
                      <th>Achieved Date</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {carePlan.goals.map((goal) => (
                      <tr key={goal.id}>
                        <td>{goal.description}</td>
                        <td>{goal.status}</td>
                        <td>{goal.targetDate ? formatDate(goal.targetDate) : '-'}</td>
                        <td>{goal.achievedDate ? formatDate(goal.achievedDate) : '-'}</td>
                        <td>{goal.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {carePlan.interventions && carePlan.interventions.length > 0 && (
              <div className="care-plan-section">
                <h2>Interventions ({carePlan.interventions.length})</h2>
                {/* Screen view - list format */}
                <div className="interventions-list screen-only">
                  {carePlan.interventions.map((intervention) => (
                    <div key={intervention.id} className="intervention-item">
                      <div className="intervention-header">
                        <span className={`intervention-type type-${intervention.type}`}>{intervention.type}</span>
                        <span className={`intervention-status status-${intervention.status}`}>{intervention.status}</span>
                      </div>
                      <div className="intervention-description">{intervention.description}</div>
                      {(intervention.frequency || intervention.duration) && (
                        <div className="intervention-details">
                          {intervention.frequency && <span>Frequency: {intervention.frequency}</span>}
                          {intervention.duration && <span>Duration: {intervention.duration}</span>}
                        </div>
                      )}
                      {(intervention.startDate || intervention.endDate) && (
                        <div className="intervention-dates">
                          {intervention.startDate && <span>Start: {formatDate(intervention.startDate)}</span>}
                          {intervention.endDate && <span>End: {formatDate(intervention.endDate)}</span>}
                        </div>
                      )}
                      {(intervention.prescriptionId || intervention.medicationId) && (
                        <div className="intervention-rx">
                          {intervention.prescriptionId && (
                            <span className="rx-link">Linked to Prescription: {intervention.prescriptionId}</span>
                          )}
                          {intervention.medicationId && (
                            <span className="rx-link">Linked to Medication: {intervention.medicationId}</span>
                          )}
                        </div>
                      )}
                      {intervention.notes && <div className="intervention-notes">{intervention.notes}</div>}
                    </div>
                  ))}
                </div>
                {/* Print view - table format */}
                <table className="print-table print-only">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Frequency</th>
                      <th>Duration</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {carePlan.interventions.map((intervention) => (
                      <tr key={intervention.id}>
                        <td>{intervention.description}</td>
                        <td>{intervention.type}</td>
                        <td>{intervention.status}</td>
                        <td>{intervention.frequency || '-'}</td>
                        <td>{intervention.duration || '-'}</td>
                        <td>{intervention.startDate ? formatDate(intervention.startDate) : '-'}</td>
                        <td>{intervention.endDate ? formatDate(intervention.endDate) : '-'}</td>
                        <td>{intervention.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {carePlan.outcomes && carePlan.outcomes.length > 0 && (
              <div className="care-plan-section">
                <h2>Outcomes ({carePlan.outcomes.length})</h2>
                {/* Screen view - list format */}
                <div className="outcomes-list screen-only">
                  {carePlan.outcomes.map((outcome) => (
                    <div key={outcome.id} className="outcome-item">
                      <div className="outcome-header">
                        <span className={`outcome-status status-${outcome.status}`}>{outcome.status}</span>
                      </div>
                      <div className="outcome-description">{outcome.description}</div>
                      {(outcome.measurement || outcome.targetValue || outcome.currentValue) && (
                        <div className="outcome-measurement">
                          {outcome.measurement && <span>Measurement: {outcome.measurement}</span>}
                          {outcome.targetValue && <span>Target: {outcome.targetValue}</span>}
                          {outcome.currentValue && <span>Current: {outcome.currentValue}</span>}
                        </div>
                      )}
                      {outcome.measuredDate && (
                        <div className="outcome-measured">Measured: {formatDate(outcome.measuredDate)}</div>
                      )}
                      {outcome.notes && <div className="outcome-notes">{outcome.notes}</div>}
                    </div>
                  ))}
                </div>
                {/* Print view - table format */}
                <table className="print-table print-only">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Measurement</th>
                      <th>Target Value</th>
                      <th>Current Value</th>
                      <th>Measured Date</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {carePlan.outcomes.map((outcome) => (
                      <tr key={outcome.id}>
                        <td>{outcome.description}</td>
                        <td>{outcome.status}</td>
                        <td>{outcome.measurement || '-'}</td>
                        <td>{outcome.targetValue || '-'}</td>
                        <td>{outcome.currentValue || '-'}</td>
                        <td>{outcome.measuredDate ? formatDate(outcome.measuredDate) : '-'}</td>
                        <td>{outcome.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="care-plan-section">
              <h3>Metadata</h3>
              <div className="detail-list">
                <div className="detail-item">
                  <label>Created</label>
                  <p>{formatDate(carePlan.createdAt, 'dd/MM/yyyy HH:mm')}</p>
                </div>
                {carePlan.createdBy && (
                  <div className="detail-item">
                    <label>Created By</label>
                    <p>{carePlan.createdBy}</p>
                  </div>
                )}
                <div className="detail-item">
                  <label>Last Updated</label>
                  <p>{formatDate(carePlan.updatedAt, 'dd/MM/yyyy HH:mm')}</p>
                </div>
                {carePlan.updatedBy && (
                  <div className="detail-item">
                    <label>Updated By</label>
                    <p>{carePlan.updatedBy}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && !carePlan.isTemplate && (
          <div className="care-plan-main">
            <CarePlanHistory carePlanId={id!} />
          </div>
        )}
      </div>

      {/* Transfer Modal */}
      {showTransferModal && id && (
        <CarePlanTransferModal
          isOpen={showTransferModal}
          onClose={() => setShowTransferModal(false)}
          carePlanId={id}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['care-plan', id] })
            setShowTransferModal(false)
          }}
        />
      )}

      {/* Create Template Modal */}
      {showCreateTemplateModal && carePlan && (
        <CarePlanCreateTemplateModal
          isOpen={showCreateTemplateModal}
          onClose={() => setShowCreateTemplateModal(false)}
          carePlan={carePlan}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['care-plan-templates'] })
          }}
        />
      )}

      {/* Edit Modal */}
      <Modal
        isOpen={showEditForm}
        onClose={() => setShowEditForm(false)}
        title="Edit Care Plan"
        size="large"
      >
        <CarePlanForm
          patientId={carePlan.patientId}
          carePlan={carePlan}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['care-plan', id] })
            setShowEditForm(false)
          }}
          onCancel={() => setShowEditForm(false)}
        />
      </Modal>
    </div>
  )
}
